import {Spec} from 'immutability-helper';
import React, {UpdateSpec} from 'react';
import {v4 as uuidv4} from 'uuid';

import {TEXT_PLUGIN_COMPONENT_BASE} from 'notebook/mediocre/base-plugin/plugins';
import {makeFormattedTextSearchResultData} from 'notebook/mediocre/formatted-text/FormattedTextSearchResults';
// @ts-expect-error
import formatters from 'notebook/mediocre/formatted-text/formatters';
import {Format} from 'notebook/mediocre/formatted-text/FormattersConstants';
import {getSearchResultsForInternalLink} from 'notebook/mediocre/search/InternalLinkSearchUtil';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import {MediocreSearchResult, TextMatcher} from 'notebook/mediocre/search/SearchResults';
import NoteConstants from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorPlugin, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {NotePart} from 'notebook/util/NoteUtilConstants';

import NoteEditable from './NoteEditable';

const NOTE_PLUGIN_COMPONENT: NoteEditorPlugin['component'] = {
  ...TEXT_PLUGIN_COMPONENT_BASE,
  required: [
    'textItemUpdateHandler',
    'textItemMergeUpHandler',
    'moveHandler',
    'noteSplitHandler',
    'multiArrowSelectHandler',
    'tabHandler',
    'getListType',
    'getHeaderType',
    'getIndentation',
    'keyDownHook',
  ],

  skipVirtualizationDueToPerformantRender(item: NotePart): boolean {
    // Skipping virtualizing since rendering a text-only component is relatively fast.
    // To test, created an entry with 200 text NoteParts. Pulled out the editor div containing all
    // the elements and profiled with Chrome profiler just rendering those parts. It takes 13ms rendering /
    // 3 ms painting for this.
    return true;
  },

  make(
    this: NoteEditor,
    item: NotePart,
    index: number,
    editorProps: NoteEditorProps,
    itemProps: undefined,
    searchResultProps: MediocreSearchResultProps
  ): JSX.Element {
    // Note: Do not directly access @props or @state or indirectly through a method call here (D5845)
    const cid = editorProps.getCid(item);
    const {text, formats} = this.getText(item);
    return (
      <NoteEditable
        key={cid}
        cid={cid}
        text={text}
        listType={this.getListType(item)}
        headerType={this.getHeaderType(item)}
        indentation={this.getIndentation(item)}
        // TODO(typescript): calculatedListIndex is augmented on a NotePart via updateCalculatedIndices
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        calculatedListIndex={(item as any).calculatedListIndex as number}
        formats={formats}
        index={index}
        pasteHandler={this.pasteHandler}
        updateHandler={this.textItemUpdateHandler}
        formatHandler={this.formatHandler}
        splitHandler={this.noteSplitHandler}
        mergeUpHandler={this.textItemMergeUpHandler}
        moveHandler={this.moveHandler}
        tabHandler={this.tabHandler}
        multiSelectHandler={this.multiArrowSelectHandler}
        keyDownHook={this.keyDownHook}
        checkHandler={this.checkHandler}
        storeState={editorProps.storeState}
        restoreState={editorProps.restoreState}
        locked={editorProps.locked}
        isProtected={this.isProtectedAgainstEdit()}
        spellCheck={editorProps.spellCheck}
        {...searchResultProps}
      />
    );
  },
};

const TEXT_PLUGIN: NoteEditorPlugin = {
  type: NoteConstants.TEXT,
  typeClass: 'is-text',
  title: 'Note',
  icon: 'note',
  component: NOTE_PLUGIN_COMPONENT,
  linkable: false,
  canPasteFromOtherTenant: true,
  itemToHTML($el: JQuery, item: NotePart): JQuery {
    $el.html(
      formatters.renderHTML(item.text, item.formats, {
        forceNonEmpty: true,
        listType: item.listType,
        indentation: item.indentation,
        // Used by formatters.renderHTML to set <OL start='...'> for numbered
        // list items so external editors keep the running count when each
        // note part is serialized as its own <OL>.
        calculatedListIndex: item.calculatedListIndex,
      })
    );
    return $el;
  },
  itemFromHTML($el: JQuery, item: NotePart): NotePart {
    return item;
  },
  itemToText(item: NotePart): string {
    return item.text;
  },
  getContent({text, formats}: {text: string; formats?: Format}): {text: string; formats: Format | undefined} {
    return {text, formats};
  },
  setContent({text, formats}: {text: string; formats: Format}): Spec<NotePart> {
    return {
      $merge: {text, formats},
    };
  },
  getText({text, formats}: {text: string; formats: Format}): {text: string; formats: Format} {
    return {text, formats};
  },
  setText({text, formats}: {text: string; formats: Format}): UpdateSpec {
    return {
      $merge: {text, formats},
    };
  },
  *getSearchResults(item: NotePart, matcher: TextMatcher): Generator<MediocreSearchResult> {
    const searchResultsByPosition: Array<{result: MediocreSearchResult; position: number}> = [];

    // Collect search results from internal links
    if (item.formats?.internalLink) {
      searchResultsByPosition.push(
        ...getSearchResultsForInternalLink(matcher, item.formats.internalLink, item.cid)
      );
    }

    // Add further search results from the note part text
    for (const match of matcher.findAllMatches(item.text)) {
      const [matchedText] = match;
      const start = match.index;
      const end = start + matchedText.length;
      searchResultsByPosition.push({
        result: {
          index: [item.cid],
          data: makeFormattedTextSearchResultData(start, end),
          selection: {selection: {cid: item.cid, start, end}},
          uuid: uuidv4(),
        },
        position: start,
      });
    }

    // Sort the results such that text and internal link results are yielded in order that they would appear
    // on the note part
    searchResultsByPosition.sort((a, b) => a.position - b.position);
    for (const resultByPosition of searchResultsByPosition) {
      yield resultByPosition.result;
    }
  },
};

export default TEXT_PLUGIN;
